export const ACTION_TYPES = Object.freeze({
  CLIMATE_ACTION: 'CLIMATE_ACTION',
  CARBON_CREDIT_RETIREMENT: 'CARBON_CREDIT_RETIREMENT',
});

export const COST_ESTIMATE_STATUS = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  RATE_REQUIRED: 'RATE_REQUIRED',
});

export const COST_ESTIMATE_KINDS = Object.freeze({
  PLANNING: 'PLANNING',
  RETIREMENT: 'RETIREMENT',
});

export const FINANCIAL_EFFECT = Object.freeze({
  ADDITIONAL_COST: 'ADDITIONAL_COST',
  SAVING: 'SAVING',
});

export const COST_RATE_BASIS = Object.freeze({
  ACTIVITY_UNIT: 'ACTIVITY_UNIT',
  RETIRED_TCO2E: 'RETIRED_TCO2E',
});

/**
 * Explicit demo-only retirement price. Climate-action rates intentionally stay
 * empty until an approved activity-price scenario is available. An emission
 * factor is not a monetary rate and must never be converted into one silently.
 */
export const DEMO_COST_RATES = Object.freeze([
  Object.freeze({
    id: 'demo-retired-credit-v1', solutionCode: null,
    actionType: ACTION_TYPES.CARBON_CREDIT_RETIREMENT,
    annualEffectType: FINANCIAL_EFFECT.ADDITIONAL_COST,
    rateBasis: COST_RATE_BASIS.RETIRED_TCO2E,
    priceJpyPerTco2e: 3000,
    currency: 'JPY', source: 'DEMO_COST_RATE_WORKBOOK_V1', version: '1',
    effectiveFromFY: 2025, effectiveToFY: 2025, approvalStatus: 'DEMO_APPROVED',
  }),
]);

const numberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const activeForYear = (rate, fiscalYear) => (
  ['APPROVED', 'DEMO_APPROVED'].includes(rate.approvalStatus)
  && Number(fiscalYear) >= rate.effectiveFromFY
  && Number(fiscalYear) <= rate.effectiveToFY
);

/** Resolve an exact solution rate before an approved Scope/category default. */
export function resolveCostRate({
  actionType = ACTION_TYPES.CLIMATE_ACTION,
  solutionCode,
  scope,
  category,
  fiscalYear,
  rates = DEMO_COST_RATES,
} = {}) {
  const candidates = rates.filter(rate => activeForYear(rate, fiscalYear));
  if (actionType === ACTION_TYPES.CARBON_CREDIT_RETIREMENT) {
    return candidates.find(rate => rate.actionType === actionType) || null;
  }
  const exact = candidates.find(rate => (
    rate.actionType !== ACTION_TYPES.CARBON_CREDIT_RETIREMENT
    && rate.solutionCode === solutionCode
    && rate.scope === scope
    && rate.category === category
  ));
  if (exact) return exact;
  return candidates.find(rate => (
    !rate.solutionCode
    && rate.actionType !== ACTION_TYPES.CARBON_CREDIT_RETIREMENT
    && rate.scope === scope
    && rate.category === category
  )) || null;
}

/** Convert one explicit carbon target with a versioned cost rate; never use an EF as money. */
export function calculateCostEstimate({
  actionType = ACTION_TYPES.CLIMATE_ACTION,
  targetTco2e,
  activityEquivalent,
  implementationCostJpy,
  costRate,
} = {}) {
  const target = numberOrNull(targetTco2e);
  const activity = numberOrNull(activityEquivalent);
  const implementationCost = numberOrNull(implementationCostJpy);
  const estimateKind = actionType === ACTION_TYPES.CARBON_CREDIT_RETIREMENT
    ? COST_ESTIMATE_KINDS.RETIREMENT
    : COST_ESTIMATE_KINDS.PLANNING;
  const retirement = estimateKind === COST_ESTIMATE_KINDS.RETIREMENT;
  const activityRate = numberOrNull(costRate?.annualRateJpyPerActivityUnit);
  const retirementPrice = numberOrNull(costRate?.priceJpyPerTco2e);
  const hasApprovedProvenance = Boolean(
    costRate?.source
    && costRate?.version
    && ['APPROVED', 'DEMO_APPROVED'].includes(costRate?.approvalStatus),
  );
  const hasApplicableRate = retirement ? retirementPrice !== null : activityRate !== null;
  const hasApplicableQuantity = retirement ? target !== null : activity !== null;
  if (!costRate || target === null || !hasApprovedProvenance
    || !hasApplicableRate || !hasApplicableQuantity) {
    return {
      status: COST_ESTIMATE_STATUS.RATE_REQUIRED,
      estimateKind,
      targetTco2e: target,
      activityEquivalent: activity,
      annualEffectType: null,
      annualAmountJpy: null,
      retirementCostJpy: null,
      implementationCostJpy: implementationCost,
      firstYearNetEffectJpy: null,
      costOrBenefitPerTco2eJpy: null,
      costRateSnapshot: costRate ? { ...costRate } : null,
    };
  }

  const ratedAmount = retirement
    ? target * retirementPrice
    : activity * activityRate;
  const annualAmountJpy = retirement ? null : ratedAmount;
  const retirementCostJpy = retirement ? ratedAmount : null;
  let firstYearNetEffectJpy = null;
  if (retirement) {
    firstYearNetEffectJpy = -ratedAmount;
  } else if (implementationCost !== null) {
    firstYearNetEffectJpy = costRate.annualEffectType === FINANCIAL_EFFECT.ADDITIONAL_COST
      ? -(ratedAmount + implementationCost)
      : ratedAmount - implementationCost;
  }

  return {
    status: COST_ESTIMATE_STATUS.AVAILABLE,
    estimateKind,
    targetTco2e: target,
    activityEquivalent: activity,
    annualEffectType: costRate.annualEffectType,
    annualAmountJpy,
    retirementCostJpy,
    implementationCostJpy: implementationCost,
    firstYearNetEffectJpy,
    costOrBenefitPerTco2eJpy: target > 0 && firstYearNetEffectJpy !== null
      ? Math.abs(firstYearNetEffectJpy) / target
      : null,
    costRateSnapshot: { ...costRate },
  };
}

/** Normalize an AI scenario into the canonical editable action estimate. */
export function normalizeRecommendationFinance(finance = {}, targetTco2e = null) {
  const estimatedCost = numberOrNull(
    finance.estimatedAnnualCostJpy ?? finance.annualCostJpy,
  );
  const estimatedSaving = numberOrNull(
    finance.estimatedAnnualSavingsJpy ?? finance.annualSavingsJpy,
  );
  const target = numberOrNull(targetTco2e);
  const implementationCostJpy = numberOrNull(finance.implementationCostJpy);
  const annualEffectType = estimatedSaving !== null
    ? FINANCIAL_EFFECT.SAVING
    : (estimatedCost !== null ? FINANCIAL_EFFECT.ADDITIONAL_COST : null);
  const annualAmountJpy = estimatedSaving ?? estimatedCost;
  const firstYearNetEffectJpy = annualAmountJpy === null || implementationCostJpy === null
    ? null
    : (annualEffectType === FINANCIAL_EFFECT.SAVING
      ? annualAmountJpy - implementationCostJpy
      : -(annualAmountJpy + implementationCostJpy));
  return {
    status: annualAmountJpy === null
      ? COST_ESTIMATE_STATUS.RATE_REQUIRED
      : COST_ESTIMATE_STATUS.AVAILABLE,
    estimateKind: COST_ESTIMATE_KINDS.PLANNING,
    targetTco2e: target,
    annualEffectType,
    annualAmountJpy,
    retirementCostJpy: null,
    implementationCostJpy,
    firstYearNetEffectJpy,
    costOrBenefitPerTco2eJpy: target > 0 && firstYearNetEffectJpy !== null
      ? Math.abs(firstYearNetEffectJpy) / target
      : null,
    costRateSnapshot: finance.costRateSnapshot || null,
    basis: finance.costSource || finance.basis || null,
  };
}
